/**
 * Hook for liveness detection in the browser.
 * Provides functions to detect blink, smile, and head turn
 * using face-api.js landmarks and expressions.
 */
import { useCallback, useState } from 'react';
import type * as faceapi from 'face-api.js';

// Challenge types for active liveness detection
export enum ChallengeType {
  BLINK = 'BLINK',
  TURN_LEFT = 'TURN_LEFT',
  TURN_RIGHT = 'TURN_RIGHT',
  SMILE = 'SMILE',
}

export interface LivenessDetectionResult {
  passed: boolean;
  score: number;
  challengeType?: ChallengeType;
}

// EAR (Eye Aspect Ratio) threshold for blink detection
const EAR_THRESHOLD = 0.21;

// Smile threshold (happy expression probability)
const SMILE_THRESHOLD = 0.7;

// Head turn yaw angle threshold in degrees
const HEAD_TURN_THRESHOLD = 15;

/**
 * Calculate Eye Aspect Ratio (EAR) from landmarks.
 * EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
 */
function calculateEAR(eyePoints: { x: number; y: number }[]): number {
  if (eyePoints.length < 6) return 1;

  const p1 = eyePoints[0];
  const p2 = eyePoints[1];
  const p3 = eyePoints[2];
  const p4 = eyePoints[3];
  const p5 = eyePoints[4];
  const p6 = eyePoints[5];

  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

  const vertical1 = dist(p2, p6);
  const vertical2 = dist(p3, p5);
  const horizontal = dist(p1, p4);

  if (horizontal === 0) return 1;

  return (vertical1 + vertical2) / (2 * horizontal);
}

/**
 * Calculate yaw angle from face landmarks.
 * Uses nose tip and face contour to estimate horizontal rotation.
 */
function calculateYawAngle(landmarks: faceapi.FaceLandmarks68): number {
  const positions = landmarks.positions;

  // Nose tip (index 30)
  const noseTip = positions[30];
  // Left face contour (index 0)
  const leftContour = positions[0];
  // Right face contour (index 16)
  const rightContour = positions[16];

  if (!noseTip || !leftContour || !rightContour) return 0;

  // Calculate face center
  const faceCenterX = (leftContour.x + rightContour.x) / 2;
  const faceWidth = rightContour.x - leftContour.x;

  if (faceWidth === 0) return 0;

  // Nose offset from center as ratio
  const noseOffset = (noseTip.x - faceCenterX) / (faceWidth / 2);

  // Convert to approximate degrees (arcsin approximation)
  const yawRadians = Math.asin(Math.max(-1, Math.min(1, noseOffset)));
  return (yawRadians * 180) / Math.PI;
}

export function useLivenessDetection() {
  const [currentChallenge, setCurrentChallenge] = useState<ChallengeType | null>(null);

  /**
   * Detect blink by calculating EAR for both eyes.
   * Returns true if EAR < threshold (eyes closed).
   */
  const detectBlink = useCallback((landmarks: faceapi.FaceLandmarks68): boolean => {
    const positions = landmarks.positions;

    // Left eye landmarks: indices 36-41
    const leftEye = [
      positions[36], positions[37], positions[38],
      positions[39], positions[40], positions[41],
    ];

    // Right eye landmarks: indices 42-47
    const rightEye = [
      positions[42], positions[43], positions[44],
      positions[45], positions[46], positions[47],
    ];

    const leftEAR = calculateEAR(leftEye);
    const rightEAR = calculateEAR(rightEye);
    const avgEAR = (leftEAR + rightEAR) / 2;

    return avgEAR < EAR_THRESHOLD;
  }, []);

  /**
   * Detect smile by checking happy expression probability.
   * Returns true if happy > threshold.
   */
  const detectSmile = useCallback((expressions: faceapi.FaceExpressions): boolean => {
    const happy = expressions.happy ?? 0;
    return happy > SMILE_THRESHOLD;
  }, []);

  /**
   * Detect head turn by calculating yaw angle from landmarks.
   * Returns 'left' if yaw < -threshold, 'right' if yaw > threshold, null otherwise.
   */
  const detectHeadTurn = useCallback(
    (landmarks: faceapi.FaceLandmarks68): 'left' | 'right' | null => {
      const yaw = calculateYawAngle(landmarks);

      if (yaw < -HEAD_TURN_THRESHOLD) return 'left';
      if (yaw > HEAD_TURN_THRESHOLD) return 'right';
      return null;
    },
    []
  );

  /**
   * Select a random challenge type for active liveness detection.
   */
  const selectRandomChallenge = useCallback((): ChallengeType => {
    const challenges = Object.values(ChallengeType);
    const index = Math.floor(Math.random() * challenges.length);
    const challenge = challenges[index];
    setCurrentChallenge(challenge);
    return challenge;
  }, []);

  /**
   * Verify if the current challenge was completed based on detection results.
   */
  const verifyChallengeCompleted = useCallback(
    (
      challengeType: ChallengeType,
      landmarks: faceapi.FaceLandmarks68,
      expressions: faceapi.FaceExpressions
    ): boolean => {
      switch (challengeType) {
        case ChallengeType.BLINK:
          return detectBlink(landmarks);
        case ChallengeType.SMILE:
          return detectSmile(expressions);
        case ChallengeType.TURN_LEFT: {
          const turn = detectHeadTurn(landmarks);
          return turn === 'left';
        }
        case ChallengeType.TURN_RIGHT: {
          const turn = detectHeadTurn(landmarks);
          return turn === 'right';
        }
        default:
          return false;
      }
    },
    [detectBlink, detectSmile, detectHeadTurn]
  );

  return {
    detectBlink,
    detectSmile,
    detectHeadTurn,
    selectRandomChallenge,
    verifyChallengeCompleted,
    currentChallenge,
    setCurrentChallenge,
  };
}
